<script setup lang="ts">
import { Divider } from "~/components/clarity-ui/Divider";
import { Heading } from "~/components/clarity-ui/Heading";
import { Tag } from "~/components/clarity-ui/Tag";
import { TagGroup } from "~/components/clarity-ui/Tag/TagGroup";
import { IconList } from "~/components/ui/IconList";
import { IconListItem } from "~/components/ui/IconList/IconListItem";
import usePromoTracking from "~/composables/tracking/usePromoTracking";
import CallToActionComponent from "~/templates/components/cms/CallToActionComponent.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import type { mgnlHeadline } from "~/types/magnolia";
import { DIMENSIONS } from "~/utils/helper/imaging";
import { containsImage, hasNodes, imagePath, mapNodes } from "~/utils/helper/magnolia";

export interface CategoryOverviewTileComponentProps {
  benefits?: object;
  headline?: mgnlHeadline;
  image?: object;
  imageGradientBottom?: boolean;
  link?: object;
  metadata?: object;
  stretchLink?: boolean;
  subline?: string;
  tags?: [];
}

const props = defineProps<CategoryOverviewTileComponentProps>();

const categoryOverviewTile = ref(null);
const { trackPromoView, trackPromoClick } = usePromoTracking({
  metadata: props?.metadata,
  creativeName: props?.headline?.text,
});

const BREAKPOINTS = {
  start: DIMENSIONS["start"]?.ratio["3/2"],
  sm: DIMENSIONS["sm"].ratio["3/2"],
  md: DIMENSIONS["md"].width["3/2"],
  xl: DIMENSIONS["xl"].width["1/2"]?.ratio["3/2"],
};

onMounted(() => {
  trackPromoView(categoryOverviewTile);
});
</script>

<template>
  <div
    ref="categoryOverviewTile"
    class="relative flex flex-col overflow-hidden rounded-xl bg-light-grey"
  >
    <div v-if="containsImage(image)" class="relative">
      <PictureElement
        :breakpoints="BREAKPOINTS"
        :img="image"
        img-class="aspect-3/2 object-cover w-full h-full overflow-hidden"
      />
      <div
        class="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black to-transparent opacity-60"
      />
      <div
        v-if="imageGradientBottom !== false"
        class="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent opacity-60"
      />
      <div class="absolute inset-0 flex flex-col justify-between p-6 xl:p-8">
        <Heading v-if="headline.text" color="light" :tag="headline?.tag ?? 'h2'">{{
          headline.text
        }}</Heading>
        <TagGroup v-if="tags?.length > 0" color="light" size="small" outlined>
          <Tag v-for="tag in tags" :key="tag['@id']">{{ tag?.value }}</Tag>
        </TagGroup>
      </div>
    </div>
    <div class="flex flex-1 flex-col gap-4 p-6 xl:gap-6 xl:p-8">
      <div v-if="subline" class="text-lg xl:text-2xl">{{ subline }}</div>
      <Divider v-if="subline" />
      <IconList v-if="hasNodes(benefits)" class="flex-1">
        <IconListItem
          v-for="benefit in mapNodes(benefits)"
          :key="benefit['@id']"
          :heading="benefit.headline"
          :image="{ src: imagePath(benefit.image) }"
          >{{ benefit.description }}</IconListItem
        >
      </IconList>
      <CallToActionComponent
        v-if="link?.label"
        class="mt-2"
        :link="link"
        :stretch-link="stretchLink"
        @click="trackPromoClick"
      />
    </div>
  </div>
</template>
